import { useState, useCallback } from "react"
import Bugsnag from "@bugsnag/js"

import type {
  AcceptJsCardData,
  AcceptJsOpaqueData,
  AcceptJsResponse,
  AcceptJsSecureData,
} from "@src/entities/types/authorize-net"

/** User-friendly error messages for Accept.js error codes */
const ERROR_MESSAGES: Record<string, string> = {
  E_WC_05: "Please check your card number",
  E_WC_06: "Please check your expiration date",
  E_WC_07: "Please check your security code (CVV)",
  E_WC_08: "This card has expired. Please use a different card.",
  E_WC_14: "Payment system is temporarily unavailable. Please try again.",
  E_WC_15: "Payment configuration error. Please contact support.",
  E_WC_16: "Payment configuration error. Please contact support.",
  E_WC_17: "Transaction failed. Please try again or use a different card.",
}

/** Get user-friendly error message from Accept.js error code */
function getErrorMessage(code: string, fallbackText: string): string {
  return ERROR_MESSAGES[code] || fallbackText || "An error occurred processing your payment"
}

/** Maximum time to wait for Accept.js to load (ms) */
const ACCEPT_JS_LOAD_TIMEOUT = 10000

/** Interval between checks for Accept.js availability (ms) */
const ACCEPT_JS_CHECK_INTERVAL = 100

/**
 * Wait for Accept.js to be available on window
 * @returns Promise that resolves when Accept is available, rejects on timeout
 */
function waitForAcceptJs(): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const checkAccept = () => {
      if (window.Accept) {
        resolve()
        return
      }

      if (Date.now() - startTime > ACCEPT_JS_LOAD_TIMEOUT) {
        reject(new Error("Accept.js failed to load. Please refresh and try again."))
        return
      }

      setTimeout(checkAccept, ACCEPT_JS_CHECK_INTERVAL)
    }

    checkAccept()
  })
}

export interface UseAuthorizeNetReturn {
  /** Tokenize card data and return opaque data for backend */
  tokenize: (cardData: AcceptJsCardData) => Promise<AcceptJsOpaqueData>
  /** Whether a tokenization request is in progress */
  isLoading: boolean
  /** Error message from last tokenization attempt (null if successful) */
  error: string | null
  /** Clear any existing error */
  clearError: () => void
}

/**
 * Custom hook for Authorize.net Accept.js card tokenization.
 *
 * This hook handles:
 * - Waiting for Accept.js script to load
 * - Tokenizing card data via Accept.dispatchData
 * - Error handling with user-friendly messages
 * - Loading state management
 *
 * @example
 * ```tsx
 * const { tokenize, isLoading, error } = useAuthorizeNet()
 *
 * const handleSubmit = async () => {
 *   try {
 *     const { dataDescriptor, dataValue } = await tokenize({
 *       cardNumber: "4111111111111111",
 *       month: "12",
 *       year: "2025",
 *       cardCode: "123"
 *     })
 *     // Send dataDescriptor and dataValue to your GraphQL mutation
 *   } catch (err) {
 *     // Error is also available via the `error` state
 *   }
 * }
 * ```
 */
export default function useAuthorizeNet(): UseAuthorizeNetReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const tokenize = useCallback(
    async (cardData: AcceptJsCardData): Promise<AcceptJsOpaqueData> => {
      setIsLoading(true)
      setError(null)

      try {
        // Wait for Accept.js to be available
        await waitForAcceptJs()

        // Build secure data object
        const secureData: AcceptJsSecureData = {
          authData: {
            clientKey: process.env.NEXT_PUBLIC_AUTHORIZE_NET_CLIENT_KEY || "",
            apiLoginID: process.env.NEXT_PUBLIC_AUTHORIZE_NET_API_LOGIN_ID || "",
          },
          cardData: {
            cardNumber: cardData.cardNumber.replace(/\s/g, ""), // Remove spaces
            month: cardData.month.padStart(2, "0"), // Ensure 2 digits
            year: cardData.year,
            cardCode: cardData.cardCode,
          },
        }

        // Validate credentials are configured
        if (!secureData.authData.clientKey || !secureData.authData.apiLoginID) {
          throw new Error(
            "Payment system is not configured. Please contact support."
          )
        }

        // Tokenize via Accept.js (promisified)
        const response = await new Promise<AcceptJsResponse>(
          (resolve) => {
            window.Accept!.dispatchData(secureData, resolve)
          }
        )

        // Handle error response
        if (response.messages.resultCode === "Error") {
          const firstError = response.messages.message[0]
          const errorMessage = getErrorMessage(firstError.code, firstError.text)

          // Log to Bugsnag for monitoring
          Bugsnag.notify(new Error(`Accept.js error: ${firstError.code}`), (event) => {
            event.addMetadata("acceptJs", {
              errorCode: firstError.code,
              errorText: firstError.text,
              allMessages: response.messages.message,
            })
          })

          setError(errorMessage)
          throw new Error(errorMessage)
        }

        // Success - return opaque data
        if (!response.opaqueData) {
          throw new Error("No payment token received. Please try again.")
        }

        return response.opaqueData
      } catch (err: any) {
        const errorMessage = err.message || "An error occurred processing your payment"
        setError(errorMessage)

        // Log unexpected errors to Bugsnag
        if (!err.message?.startsWith("Please") && !err.message?.startsWith("This card")) {
          Bugsnag.notify(err)
        }

        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  return {
    tokenize,
    isLoading,
    error,
    clearError,
  }
}

